// components/PostActionBar.js
import React from "react";
import {
  IconComment,
  IconRepost,
  IconLike,
  IconViews,
  IconShare,
  IconSupport,
} from "../components/FeedIcons";

export default function PostActionBar({
  post,
  navigate,
  location,
  setRepostMenu,
  onLikePost,
  myChatId,
  setMenu,
  onSupportPost,    // ← ({ post }) => void  — open "send support" modal (other users' posts)
  onViewEarnings,   // ← ({ post }) => void  — open "earnings" view (own posts)
}) {
  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleComment = (e) => {
    stop(e);
    navigate(location.pathname, {
      state: {
        view: "post",
        post,
        autoFocusComment: true,
        replyIntentId: crypto.randomUUID(),
        quoteTarget: { type: "post", post },
      },
    });
  };

  const handleRepost = (e) => {
    stop(e);
    setRepostMenu({ open: true, post });
  };

  const handleLike = (e) => {
    stop(e);
    onLikePost({ postId: post.postId, chatId: myChatId });
  };

  const handleShare = (e) => {
    stop(e);
    if (setMenu) {
      setMenu({ open: true, post, type: "share" });
    } else {
      const url = `${window.location.origin}/post/${post.postId}`;
      if (navigator.share) {
        navigator.share({ url }).catch(() => {});
      } else if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(url);
      }
    }
  };

  const isAuthor =
    myChatId &&
    String(post.author?.chatId) === String(myChatId);

  const handleSupport = (e) => {
    stop(e);
    if (isAuthor) {
      onViewEarnings?.({ post });
    } else {
      onSupportPost?.({ post });
    }
  };

  const fmt = (n) =>
    !n ? null : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);

  // Show the support/USWD icon for everyone:
  //  - other people's posts → "send support" (requires onSupportPost)
  //  - own posts            → "view earnings" (requires onViewEarnings)
  const showSupport =
    (isAuthor && onViewEarnings) ||
    (!isAuthor && onSupportPost && myChatId);

  return (
        <div
      className="mt-0.5 flex items-center text-neutral-500 dark:text-neutral-400"
      onClick={stop}
    >
      {/* LEFT CLUSTER */}
      <div className="flex items-center justify-between flex-1 pr-2">

        {/* COMMENT */}
        <button
          type="button"
          onClick={handleComment}
          className="flex items-center gap-1.5 group touch-manipulation"
          style={{ minHeight: 32, minWidth: 36 }}
          aria-label="Comment"
        >
          <span className="group-hover:text-blue-500 group-active:scale-90 transition-all duration-100">
            <IconComment />
          </span>
          {fmt(post.commentsCount) && (
            <span className="text-xs group-hover:text-blue-500 tabular-nums leading-none">
              {fmt(post.commentsCount)}
            </span>
          )}
        </button>

        {/* REPOST */}
        <button
          type="button"
          onClick={handleRepost}
          className="flex items-center gap-1.5 group touch-manipulation"
          style={{ minHeight: 32, minWidth: 36 }}
          aria-label="Repost"
        >
          <span
            className={`group-hover:text-green-500 group-active:scale-90 transition-all duration-100 ${
              post.repostedByMe ? "text-green-500" : ""
            }`}
          >
            <IconRepost />
          </span>
          {fmt(post.repostsCount) && (
            <span
              className={`text-xs tabular-nums leading-none group-hover:text-green-500 ${
                post.repostedByMe ? "text-green-500" : ""
              }`}
            >
              {fmt(post.repostsCount)}
            </span>
          )}
        </button>

        {/* LIKE */}
        <button
          type="button"
          onClick={handleLike}
          className="flex items-center gap-1.5 group touch-manipulation"
          style={{ minHeight: 32, minWidth: 36 }}
          aria-label="Like"
        >
                   <span
            className={`group-active:scale-90 transition-all duration-100 [&>svg]:w-5 [&>svg]:h-4 ${
              post.likedByMe ? "text-red-500" : "group-hover:text-red-500"
            }`}
          >
            <IconLike filled={post.likedByMe} />
          </span>
          {fmt(post.likesCount) && (
            <span
              className={`text-xs tabular-nums leading-none ${
                post.likedByMe ? "text-red-500" : "group-hover:text-red-500"
              }`}
            >
              {fmt(post.likesCount)}
            </span>
          )}
        </button>

        {/* VIEWS — non-interactive */}
        <div
          className="flex items-center gap-1.5 select-none"
          style={{ minHeight: 32 }}
          aria-label={`${post.viewsCount || 0} views`}
        >
          <IconViews />
          <span className="text-xs tabular-nums leading-none">
            {fmt(post.viewsCount) || "0"}
          </span>
        </div>

      {/* SUPPORT / TIP — send for others' posts, earnings view for own posts */}
        {showSupport && (
          <button
            type="button"
            onClick={handleSupport}
            className="flex items-center gap-1.5 group touch-manipulation"
            style={{ minHeight: 32, minWidth: 36 }}
            aria-label={isAuthor ? "View earnings" : "Support with USWD"}
            title={isAuthor ? "View earnings" : "Support with USWD"}
          >
            <span className="group-hover:text-yellow-500 group-active:scale-90 transition-all duration-100">
              <IconSupport />
            </span>
            {fmt(post.supportsCount) && (
              <span className="text-xs tabular-nums leading-none group-hover:text-yellow-500">
                {fmt(post.supportsCount)}
              </span>
            )}
          </button>
        )}
      </div>

      {/* SHARE */}
      <button
        type="button"
        onClick={handleShare}
        className="flex items-center justify-center hover:text-blue-500 active:scale-90 transition-all duration-100 touch-manipulation"
        style={{ minHeight: 32, minWidth: 36 }}
        title="Share"
        aria-label="Share"
      >
        <IconShare />
      </button>
    </div>
  );
}